import React, { useMemo, useState } from "react";
import { Formik, Form, useField } from "formik";
import { TurnstileWidget } from "./TurnstileWidget";
import { buildPdpAuditFormSchema, pdpAuditInitialValues, PdpAuditFormValues, normalizeUrl } from "./schema";
import { GOLD } from "../ProposalFlow/theme";

export interface PdpAuditFormSubmitArgs {
    url: string;
    captchaToken: string | null;
    email?: string;
}

interface Props {
    onSubmit: (args: PdpAuditFormSubmitArgs) => Promise<void>;
    showCaptcha?: boolean;
    disabled?: boolean;
    submittingLabel?: string;
    requireEmail?: boolean;
}

// Hover shade — brand primary-dark under RMN theming, TDC hover gold otherwise.
const GOLD_DARK = "var(--rmn-primary-dark, #B8956A)";
// Neutral roles follow ProposalFlow/theme.ts: literal = light/public value,
// --tool-* exists only in the portal's dark mode (src/theme/tokens.ts).
const BLACK = "var(--tool-text, #1C1A17)";
// Label/spinner on the gold button — white in both themes.
const WHITE = "#ffffff";
// Input plate.
const CARD = "var(--tool-card, #ffffff)";
const BORDER = "var(--tool-border, #E5E1DB)";
const BORDER_DISABLED = "var(--tool-border, #E5E1DB)";
const ERROR = "var(--tool-down, #c62828)";
const GRAY = "var(--tool-text-2, #7A7268)";
const TEXT_PLACEHOLDER = "var(--tool-muted, #C5C0B8)";
const FONT = "'DM Sans', sans-serif";

const fieldLabelStyle: React.CSSProperties = {
    fontFamily: FONT,
    fontSize: 13,
    fontWeight: 600,
    color: BLACK,
    display: "block",
    marginBottom: 8
};

const fieldErrorStyle: React.CSSProperties = {
    fontFamily: FONT,
    fontSize: 12,
    color: ERROR,
    marginTop: 6
};

function StyledUrlField({ disabled }: { disabled: boolean }) {
    const [field, meta] = useField<string>("url");
    const showError = Boolean(meta.touched && meta.error);
    const [focused, setFocused] = useState(false);
    const borderColor = showError ? ERROR : focused ? GOLD : BORDER;
    return (
        <div style={{ marginBottom: 20 }}>
            <label htmlFor="pdp-audit-url" style={fieldLabelStyle}>
                Product page URL
            </label>
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    border: `1.5px solid ${borderColor}`,
                    borderRadius: 12,
                    background: CARD,
                    overflow: "hidden",
                    transition: "border-color 0.15s"
                }}
            >
                <div style={{ padding: "0 0 0 14px", color: "var(--tool-muted, #B5AFA5)", display: "flex", alignItems: "center", flexShrink: 0 }}>
                    <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                    >
                        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                    </svg>
                </div>
                <input
                    {...field}
                    id="pdp-audit-url"
                    type="url"
                    disabled={disabled}
                    onFocus={() => setFocused(true)}
                    onBlur={(e) => {
                        setFocused(false);
                        field.onBlur(e);
                    }}
                    placeholder="https://yourbrand.com/products/your-product"
                    style={{
                        flex: 1,
                        border: "none",
                        background: "transparent",
                        padding: "14px",
                        fontFamily: FONT,
                        fontSize: 14,
                        color: BLACK,
                        outline: "none"
                    }}
                />
            </div>
            {showError && <div style={fieldErrorStyle}>{meta.error}</div>}
            <style>{`#pdp-audit-url::placeholder { color: ${TEXT_PLACEHOLDER}; }`}</style>
        </div>
    );
}

function MarketingConsentCheckbox({ disabled }: { disabled: boolean }) {
    const [field, meta, helpers] = useField<boolean>("marketingConsent");
    const showError = Boolean(meta.touched && meta.error);
    return (
        <div style={{ marginBottom: 20 }}>
            <label
                htmlFor="pdp-audit-marketing-consent"
                style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 10,
                    fontFamily: FONT,
                    fontSize: 13,
                    color: BLACK,
                    cursor: disabled ? "not-allowed" : "pointer",
                    lineHeight: 1.5
                }}
            >
                <input
                    id="pdp-audit-marketing-consent"
                    type="checkbox"
                    checked={!!field.value}
                    onChange={(e) => helpers.setValue(e.target.checked)}
                    onBlur={() => helpers.setTouched(true)}
                    disabled={disabled}
                    style={{
                        marginTop: 3,
                        width: 16,
                        height: 16,
                        accentColor: GOLD,
                        flexShrink: 0
                    }}
                />
                <span>
                    I agree to receive marketing emails from The Desire Company about audits, video production, and
                    related services. I can unsubscribe at any time.
                </span>
            </label>
            {showError && <div style={{ ...fieldErrorStyle, marginLeft: 26 }}>{meta.error}</div>}
        </div>
    );
}

function StyledEmailField({ disabled }: { disabled: boolean }) {
    const [field, meta] = useField<string>("email");
    const showError = Boolean(meta.touched && meta.error);
    const [focused, setFocused] = useState(false);
    const borderColor = showError ? ERROR : focused ? GOLD : BORDER;
    return (
        <div style={{ marginBottom: 20 }}>
            <label htmlFor="pdp-audit-email" style={fieldLabelStyle}>
                Company Email
            </label>
            <input
                {...field}
                id="pdp-audit-email"
                type="email"
                disabled={disabled}
                onFocus={() => setFocused(true)}
                onBlur={(e) => {
                    setFocused(false);
                    field.onBlur(e);
                }}
                placeholder="you@yourbrand.com"
                style={{
                    width: "100%",
                    padding: "14px 16px",
                    fontFamily: FONT,
                    fontSize: 14,
                    border: `1.5px solid ${borderColor}`,
                    borderRadius: 12,
                    outline: "none",
                    boxSizing: "border-box",
                    color: BLACK,
                    background: CARD,
                    transition: "border-color 0.15s"
                }}
            />
            {showError && <div style={fieldErrorStyle}>{meta.error}</div>}
        </div>
    );
}

function CaptchaContainer({ children }: { children: React.ReactNode }) {
    return (
        <div
            style={{
                marginBottom: 20,
                display: "flex",
                justifyContent: "center"
            }}
        >
            {children}
        </div>
    );
}

export function PdpAuditForm({
    onSubmit,
    showCaptcha = true,
    disabled = false,
    submittingLabel = "Generating audit…",
    requireEmail = false
}: Props) {
    const [captchaToken, setCaptchaToken] = useState<string | null>(null);

    const canSubmitCaptcha = showCaptcha ? !!captchaToken : true;

    const validationSchema = useMemo(() => buildPdpAuditFormSchema(requireEmail), [requireEmail]);

    return (
        <Formik<PdpAuditFormValues>
            initialValues={pdpAuditInitialValues}
            validationSchema={validationSchema}
            onSubmit={async (values, { setSubmitting }) => {
                try {
                    await onSubmit({
                        url: normalizeUrl(values.url),
                        captchaToken,
                        email: values.email.trim() || undefined
                    });
                } finally {
                    setSubmitting(false);
                }
            }}
        >
            {({ isSubmitting, values }) => {
                const buttonDisabled =
                    disabled ||
                    isSubmitting ||
                    !canSubmitCaptcha ||
                    !values.url.trim() ||
                    (requireEmail && !values.email.trim()) ||
                    (requireEmail && !values.marketingConsent);
                return (
                    <Form noValidate>
                        <StyledUrlField disabled={disabled || isSubmitting} />

                        {requireEmail && <StyledEmailField disabled={disabled || isSubmitting} />}
                        {requireEmail && <MarketingConsentCheckbox disabled={disabled || isSubmitting} />}

                        {showCaptcha && (
                            <CaptchaContainer>
                                <div style={{ width: "100%" }}>
                                    <TurnstileWidget onToken={setCaptchaToken} theme="light" size="flexible" />
                                </div>
                            </CaptchaContainer>
                        )}

                        <button
                            type="submit"
                            disabled={buttonDisabled}
                            style={{
                                width: "100%",
                                padding: 14,
                                borderRadius: 12,
                                border: "none",
                                background: buttonDisabled ? BORDER_DISABLED : GOLD,
                                color: buttonDisabled ? "var(--tool-muted, #B5AFA5)" : WHITE,
                                fontFamily: FONT,
                                fontSize: 14,
                                fontWeight: 600,
                                letterSpacing: 0.5,
                                textTransform: "uppercase",
                                cursor: buttonDisabled ? "not-allowed" : "pointer",
                                transition: "all 0.2s",
                                boxShadow: buttonDisabled ? "none" : "0 1px 0 rgba(0,0,0,0.02)"
                            }}
                            onMouseEnter={(e) => {
                                if (!buttonDisabled) {
                                    (e.currentTarget as HTMLButtonElement).style.background = GOLD_DARK;
                                    (e.currentTarget as HTMLButtonElement).style.boxShadow =
                                        "0 4px 12px rgba(200,169,126,0.3)";
                                    (e.currentTarget as HTMLButtonElement).style.transform = "translateY(-1px)";
                                }
                            }}
                            onMouseLeave={(e) => {
                                if (!buttonDisabled) {
                                    (e.currentTarget as HTMLButtonElement).style.background = GOLD;
                                    (e.currentTarget as HTMLButtonElement).style.boxShadow =
                                        "0 1px 0 rgba(0,0,0,0.02)";
                                    (e.currentTarget as HTMLButtonElement).style.transform = "translateY(0)";
                                }
                            }}
                        >
                            {isSubmitting ? (
                                <span
                                    style={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: 8,
                                        justifyContent: "center"
                                    }}
                                >
                                    <span
                                        aria-hidden
                                        style={{
                                            width: 14,
                                            height: 14,
                                            border: `2px solid ${WHITE}`,
                                            borderTopColor: "transparent",
                                            borderRadius: "50%",
                                            animation: "tdcBtnSpin 0.8s linear infinite",
                                            display: "inline-block"
                                        }}
                                    />
                                    <span>{submittingLabel}</span>
                                </span>
                            ) : (
                                "Run audit"
                            )}
                        </button>
                        <style>{`@keyframes tdcBtnSpin { to { transform: rotate(360deg); } }`}</style>
                    </Form>
                );
            }}
        </Formik>
    );
}
